// src/admin/pages/DeliveryChargePage.tsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import AdminLayout from '../components/Layout/AdminLayout';
import { FaEye, FaSearch } from 'react-icons/fa';
import Notification from '../components/Notification/Notification';
// Assuming OrderDetailsModal is a shared component as it was imported in other order pages
import OrderDetailsModal from '../components/OrderDetails/ViewOrderDetails';

// Define interfaces for Order and related nested objects
interface Customer {
  id: number;
  name: string;
}

interface ProductItem {
  productId: number;
  productName: string;
  quantity: number;
  pricePerUnit: number;
}

interface Order {
  id: number;
  customer: Customer;
  orderDate: string;
  totalAmount: number;
  orderStatus: 'PENDING' | 'PROCESSING' | 'SHIPPED' | 'DELIVERED' | 'CANCELLED' | 'REFUND';
  paymentStatus: 'PAID' | 'UNPAID' | 'REFUND_PENDING' | 'REFUNDED';
  shippingStatus: 'READY_FOR_DISPATCH' | 'IN_TRANSIT' | 'OUT_FOR_DELIVERY' | 'DELIVERED';
  deliveryAddress: string;
  products: ProductItem[];
  // New fields for DeliveryChargePage
  deliveryCharge: number;
  deliveryPartner: string;
}

const DeliveryChargePage: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' | 'info'; } | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);

  const showNotification = (message: string, type: 'success' | 'error' | 'info') => {
    setNotification({ message, type });
  };

  // More comprehensive dummy data with delivery charges and partners
  const allDummyOrders: Order[] = useMemo(() => ([
    {
      id: 1, customer: { id: 101, name: 'John Doe' }, orderDate: '2025-07-01T14:30:00', totalAmount: 120.50,
      orderStatus: 'DELIVERED', paymentStatus: 'PAID', shippingStatus: 'DELIVERED',
      deliveryAddress: '123 Main St, Anytown, USA 12345', deliveryCharge: 7.50, deliveryPartner: 'SpeedyDeliver',
      products: [
        { productId: 101, productName: 'Organic Apples (1kg)', quantity: 2, pricePerUnit: 3.50 },
        { productId: 102, productName: 'Whole Wheat Bread', quantity: 1, pricePerUnit: 4.25 },
        { productId: 105, productName: 'Milk (1 Litre)', quantity: 3, pricePerUnit: 2.50 },
      ]
    },
    {
      id: 2, customer: { id: 102, name: 'Jane Smith' }, orderDate: '2025-06-28T09:15:00', totalAmount: 75.00,
      orderStatus: 'DELIVERED', paymentStatus: 'PAID', shippingStatus: 'DELIVERED',
      deliveryAddress: '456 Oak Ave, Somewhere, USA 67890', deliveryCharge: 5.00, deliveryPartner: 'ExpressLogistics',
      products: [
        { productId: 201, productName: 'Laptop Stand', quantity: 1, pricePerUnit: 45.00 },
        { productId: 202, productName: 'Wireless Mouse', quantity: 1, pricePerUnit: 30.00 }
      ]
    },
    {
      id: 3, customer: { id: 103, name: 'Alice Johnson' }, orderDate: '2025-07-02T11:00:00', totalAmount: 230.10,
      orderStatus: 'DELIVERED', paymentStatus: 'PAID', shippingStatus: 'DELIVERED',
      deliveryAddress: '789 Pine Ln, Another City, USA 98765', deliveryCharge: 12.00, deliveryPartner: 'GlobalParcel',
      products: [
        { productId: 301, productName: 'Coffee Maker', quantity: 1, pricePerUnit: 150.00 },
        { productId: 302, productName: 'Coffee Beans (500g)', quantity: 2, pricePerUnit: 40.05 }
      ]
    },
    {
      id: 4, customer: { id: 104, name: 'Bob Williams' }, orderDate: '2025-06-25T16:45:00', totalAmount: 45.75,
      orderStatus: 'PENDING', paymentStatus: 'UNPAID', shippingStatus: 'READY_FOR_DISPATCH', 
      deliveryAddress: '101 Elm St, Smallville, USA 54321', deliveryCharge: 6.50, deliveryPartner: 'LocalCouriers',
      products: [
        { productId: 401, productName: 'T-shirt (Large)', quantity: 3, pricePerUnit: 15.25 }
      ]
    },
    {
      id: 5, customer: { id: 105, name: 'Charlie Brown' }, orderDate: '2025-06-20T08:00:00', totalAmount: 18.99,
      orderStatus: 'CANCELLED', paymentStatus: 'UNPAID', shippingStatus: 'READY_FOR_DISPATCH', 
      deliveryAddress: '202 Maple Dr, Big City, USA 11223', deliveryCharge: 0.00, deliveryPartner: 'N/A (Cancelled)',
      products: [
        { productId: 501, productName: 'Socks (3-pack)', quantity: 1, pricePerUnit: 18.99 }
      ]
    },
    {
      id: 6, customer: { id: 106, name: 'Diana Prince' }, orderDate: '2025-06-15T10:00:00', totalAmount: 300.00,
      orderStatus: 'REFUND', paymentStatus: 'REFUNDED', shippingStatus: 'DELIVERED', 
      deliveryAddress: '303 Willow Rd, Capital City, USA 33445', deliveryCharge: 15.00, deliveryPartner: 'SpeedyDeliver',
      products: [
        { productId: 601, productName: 'Smartwatch', quantity: 1, pricePerUnit: 300.00 }
      ]
    },
    {
      id: 7, customer: { id: 107, name: 'Eve Adams' }, orderDate: '2025-07-03T12:00:00', totalAmount: 50.25,
      orderStatus: 'SHIPPED', paymentStatus: 'PAID', shippingStatus: 'IN_TRANSIT',
      deliveryAddress: '404 Birch Blvd, Green Town, USA 66778', deliveryCharge: 8.00, deliveryPartner: 'ExpressLogistics',
      products: [
        { productId: 701, productName: 'Bluetooth Speaker', quantity: 1, pricePerUnit: 50.25 }
      ]
    },
    {
      id: 8, customer: { id: 108, name: 'Frank White' }, orderDate: '2025-07-04T15:00:00', totalAmount: 99.99,
      orderStatus: 'PROCESSING', paymentStatus: 'PAID', shippingStatus: 'READY_FOR_DISPATCH',
      deliveryAddress: '505 Cedar Ct, Metropolis, USA 00001', deliveryCharge: 9.25, deliveryPartner: 'GlobalParcel',
      products: [
        { productId: 801, productName: 'Gaming Headset', quantity: 1, pricePerUnit: 99.99 }
      ]
    },
    {
      id: 9, customer: { id: 109, name: 'Grace Green' }, orderDate: '2025-07-01T09:00:00', totalAmount: 15.00,
      orderStatus: 'DELIVERED', paymentStatus: 'PAID', shippingStatus: 'DELIVERED',
      deliveryAddress: '606 Spruce St, Riverton, USA 22334', deliveryCharge: 4.50, deliveryPartner: 'LocalCouriers',
      products: [
        { productId: 101, productName: 'Organic Apples (1kg)', quantity: 1, pricePerUnit: 3.50 },
        { productId: 901, productName: 'Notebook', quantity: 2, pricePerUnit: 5.75 }
      ]
    },
    {
      id: 10, customer: { id: 110, name: 'Harry Blue' }, orderDate: '2025-07-05T10:30:00', totalAmount: 150.00,
      orderStatus: 'SHIPPED', paymentStatus: 'PAID', shippingStatus: 'IN_TRANSIT',
      deliveryAddress: '707 Poplar Pkwy, Hillside, USA 44556', deliveryCharge: 10.00, deliveryPartner: 'SpeedyDeliver',
      products: [
        { productId: 1001, productName: 'Backpack', quantity: 1, pricePerUnit: 150.00 }
      ]
    },
  ]), []);

  // Simulate fetching data
  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      setOrders(allDummyOrders);
      setLoading(false);
      showNotification('Delivery charges data loaded successfully!', 'success');
    }, 500);
    return () => clearTimeout(timer);
  }, [allDummyOrders]);

  const filteredOrders = useMemo(() => {
    if (!searchQuery) {
      return orders;
    }
    return orders.filter(order =>
      order.id.toString().includes(searchQuery) ||
      order.customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.deliveryPartner.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [orders, searchQuery]);

  const handleViewDetails = useCallback((order: Order) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
  }, []);

  const handleCloseDetailsModal = useCallback(() => {
    setShowDetailsModal(false);
    setSelectedOrder(null);
  }, []);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-48 bg-gray-50 dark:bg-gray-900 min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 dark:border-green-400"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container mx-auto p-6 min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-6">
            <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-4 md:mb-0">
                Delivery Charges Report
            </h2>
        </div>

        {notification && (
          <Notification
            message={notification.message}
            type={notification.type}
            onClose={() => setNotification(null)}
          />
        )}

        {/* Search Bar */}
        <div className="mb-6 bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="relative">
            <input
              type="text"
              placeholder="Search by Order ID, Customer Name, or Delivery Partner..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-3 w-full rounded-lg outline-none focus:ring-2 focus:ring-green-500 transition-colors
              bg-gray-50 border border-gray-300 text-gray-900 placeholder-gray-500
              dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="text-gray-400 dark:text-gray-500" />
            </div>
          </div>
        </div>

        {/* Delivery Charges Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700 transition-colors">
          <div className="overflow-x-auto">
            <table className="min-w-full leading-normal">
              <thead className="bg-gray-100 dark:bg-gray-700">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Order ID
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Customer Name
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Total Amount
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Delivery Partner
                  </th>
                  <th className="px-5 py-3 text-left text-xs font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Delivery Charge
                  </th>
                  <th className="px-5 py-3 text-center text-xs font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredOrders.length > 0 ? (
                  filteredOrders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 bg-white dark:bg-gray-800 transition-colors">
                      <td className="px-5 py-4 text-sm">
                        <p className="text-gray-900 dark:text-white font-medium whitespace-no-wrap">#{order.id}</p>
                      </td>
                      <td className="px-5 py-4 text-sm">
                        <p className="text-gray-900 dark:text-white whitespace-no-wrap">{order.customer.name}</p>
                      </td>
                      <td className="px-5 py-4 text-sm">
                        <p className="text-gray-900 dark:text-white font-semibold whitespace-no-wrap">₹{order.totalAmount.toFixed(2)}</p>
                      </td>
                      <td className="px-5 py-4 text-sm">
                        <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            {order.deliveryPartner}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-sm">
                        <p className="text-red-600 dark:text-red-400 font-bold whitespace-no-wrap">₹{order.deliveryCharge.toFixed(2)}</p>
                      </td>
                      <td className="px-5 py-4 text-sm text-center">
                        <button
                          onClick={() => handleViewDetails(order)}
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors p-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-full"
                          title="View Details"
                        >
                          <FaEye className="inline-block text-lg" />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-5 py-10 text-center text-sm text-gray-500 dark:text-gray-400 font-medium">
                      No delivery charge data found matching your criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Order Details Modal */}
      {showDetailsModal && selectedOrder && (
        <OrderDetailsModal order={selectedOrder} onClose={handleCloseDetailsModal} />
      )}
    </AdminLayout>
  );
};

export default DeliveryChargePage;